import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useState, useEffect } from 'react';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { TextBlock } from '../../../../Atoms/Text';
import { SearchBox } from '../../../../Compounds/Search/Search';
import { EntityType, SearchResult } from '../../../../../store/Search';
import { createCustomerMap } from '../../../../../store/CustomerMap';

interface NewMapDialogProps {
  open: boolean;
  onClose: () => void;
  rpmCustomerPrefill?: OptionCustomer;
}

interface OptionCustomer {
  id: string;
  name: string;
}

export const NewMapDialog = ({ open, onClose, rpmCustomerPrefill }: NewMapDialogProps) => {
  const [loading, setLoading] = useState(false);

  const [rpmCustomer, setRpmCustomer] = useState<OptionCustomer | null>(rpmCustomerPrefill || null);
  const [sfdcCustomer, setSfdcCustomer] = useState<OptionCustomer | null>(null);

  useEffect(() => {
    if (open) {
      setRpmCustomer(rpmCustomerPrefill || null);
      setSfdcCustomer(null);
    }
  }, [open]);

  const handleSelect = (result: SearchResult) => {
    const { id, title, source } = result;
    if (source === 'customer') setSfdcCustomer({ id: id, name: title });
    if (source === 'rpm_customer') setRpmCustomer({ id: id, name: title });
  };

  const handleSave = async () => {
    if (!rpmCustomer || !sfdcCustomer) return;
    setLoading(true);
    await createCustomerMap({
      customerId: +rpmCustomer.id,
      sfdcCustomerUid: sfdcCustomer.id
    });
    setLoading(false);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          },
          sx: { overflowY: 'visible', maxWidth: 700, padding: 1 }
        }
      }}
      fullWidth
      maxWidth="md"
      data-testid="edit-relationship-modal">
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          New Customer Mapping
        </TextBlock>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 70, overflowY: 'visible', marginBottom: 4, marginTop: 4 }}>
        {loading && <ComponentSpinner label="Saving..." />}
        {!loading && (
          <div className="flex flex-row gap-x-10 w-full">
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">Salesforce Customer</TextBlock>
              <SearchBox
                handleSelect={handleSelect}
                searchEntityType={EntityType.CUSTOMER}
                inputString=""
                fullWidth
                placeholder=""
                handleClearSelect={() => setSfdcCustomer(null)}
              />
            </div>
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">RPM Customer</TextBlock>
              {rpmCustomerPrefill ? (
                <TextBlock
                  color="black"
                  className="pt-1 pb-2">
                  {rpmCustomerPrefill.name}
                </TextBlock>
              ) : (
                <SearchBox
                  handleSelect={handleSelect}
                  searchEntityType={EntityType.RPM_CUSTOMER_MAP}
                  inputString=""
                  fullWidth
                  placeholder=""
                  handleClearSelect={() => setRpmCustomer(null)}
                />
              )}
            </div>
          </div>
        )}
      </DialogContent>
      <hr className="mb-1 mx-6" />
      {!loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={!rpmCustomer || !sfdcCustomer}
            type="submit"
            variant="contained">
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
